/**
 * Last sign-in recall.
 *
 * Remembers, per-device, how the user last authenticated so the login screen
 * can offer a fast "Welcome back" experience (one-tap continue with the last
 * method, pre-filled email, org logo). Stored in localStorage; the canonical
 * cross-device copy lives on `profiles.last_auth_method` and is written by
 * the auth layer on sign-in.
 *
 * No tokens or secrets are stored here, only a method hint and the email,
 * which the browser already remembers via autocomplete.
 */

export type AuthMethod = "microsoft" | "password";

export interface LastSignIn {
  method: AuthMethod;
  /** Email used (password sign-in) or returned by the IdP (Microsoft). */
  email?: string;
  /** Display name, when known, for a friendlier greeting. */
  name?: string;
  /** ISO timestamp of the last successful sign-in. */
  at: string;
}

const STORAGE_KEY = "seq_claims_last_signin";

export function getLastSignIn(): LastSignIn | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastSignIn>;
    if (parsed?.method !== "microsoft" && parsed?.method !== "password") {
      return null;
    }
    return {
      method: parsed.method,
      email: typeof parsed.email === "string" ? parsed.email : undefined,
      name: typeof parsed.name === "string" ? parsed.name : undefined,
      at: typeof parsed.at === "string" ? parsed.at : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function setLastSignIn(value: Omit<LastSignIn, "at"> & { at?: string }): void {
  try {
    const record: LastSignIn = {
      method: value.method,
      email: value.email,
      name: value.name,
      at: value.at ?? new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable (private mode / quota), recall simply won't persist */
  }
}

export function clearLastSignIn(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/** Human-friendly label for a method. */
export function authMethodLabel(method: AuthMethod): string {
  return method === "microsoft" ? "Microsoft" : "email & password";
}

/** Masks an email for display: jane.doe@acme.com -> j••••e@acme.com */
export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain || local.length === 0) return email;
  if (local.length <= 2) return `${local[0]}••@${domain}`;
  return `${local[0]}${"•".repeat(Math.min(local.length - 2, 4))}${local[local.length - 1]}@${domain}`;
}
